import type { AlignTypes, AlignYTypes } from '@etg/wings';
import { Inline, vars, Badge } from '@etg/wings';
import { Icon } from '@eti/components';
import { useTranslation } from '@eti/providers';
import { css, cx } from '@eti/styles';
import { Bed, Camera, ShoppingBag } from '@phosphor-icons/react';
import type { ReactElement } from 'react';
import {
  ONE_DAY_STOPOVER,
  SHORT_STOPOVER,
} from '../../../../../../constants/searchFiltersConstants';
import { eventSegmentTitlesKeys } from '../../../../../../utils/format';

const badgeWrapperStyles = css`
  align-items: center;
  border-radius: 8px;
  display: flex;
  gap: 4px;
  min-width: max-content;
  padding: 4px 8px 4px 10px;
`;

const stopoverInfoWrapperStyles = css`
  background: #c2e2ff;
  color: #0057a7;
`;

const stopoverInfoStyles = css`
  align-items: center;
  color: #0057a7;
  font-weight: 700;
`;

const spanTextNoWrap = css`
  white-space: nowrap;
`;

interface SelfTransferIndicatorProps {
  alignX?: AlignTypes;
  alignY?: AlignYTypes;
  boundNumber: number;
}

interface StopoverInfoIndicatorProps {
  stopoverType: string;
}

const StopoverInfoIndicator = ({ stopoverType }: StopoverInfoIndicatorProps) => {
  const { t } = useTranslation();
  const eventSegmentTitles = Object.fromEntries(
    Object.entries(eventSegmentTitlesKeys).map(([key, value]) => [key, t(value)]),
  );
  let icon: ReactElement;

  switch (stopoverType) {
    case SHORT_STOPOVER:
      icon = <ShoppingBag size="1rem" weight="fill" />;
      break;
    case ONE_DAY_STOPOVER:
      icon = <Camera size="1rem" weight="fill" />;
      break;
    default:
      icon = <Bed size="1rem" weight="fill" />;
  }

  return (
    <>
      {stopoverType && (
        <Badge
          className={cx(badgeWrapperStyles, stopoverInfoWrapperStyles)}
          data-testid="trip-stopover-info-wrapper"
        >
          <Inline
            className={stopoverInfoStyles}
            data-testid={`trip-bound-${stopoverType}-info`}
            noWrap
            spacing={4}
          >
            {icon} <span className={spanTextNoWrap}>{eventSegmentTitles[stopoverType]}</span>
          </Inline>
        </Badge>
      )}
    </>
  );
};

const SelfTransferIndicator = (props: SelfTransferIndicatorProps) => {
  const { alignX = 'center', alignY = 'center', boundNumber } = props;
  const { t } = useTranslation();

  return (
    <Inline align={alignX} alignY={alignY} spacing={8}>
      <Badge
        className={badgeWrapperStyles}
        data-testid={`trip-bound-${boundNumber}-selfTransfer-info`}
        variant="caution"
      >
        <Icon color={vars.colors.signal.caution.foreground} id="self-transfer" size="1.125rem" />
        <span>{t('TripDetails.Details.SelfTransfer')}</span>
      </Badge>
    </Inline>
  );
};

export { SelfTransferIndicator, StopoverInfoIndicator };
